import {
  type Box,
  type Point,
  STEM_EM,
  type TraceStroke,
  penMarkerRadius,
  pointsFrom,
  pointsUpTo,
  strokeLength,
} from "@ai-teach/canvas-core";
import { useEffect, useRef, useState } from "react";
import { WRITING_FONT } from "./handWritingLayout";
import { useGuidedTrace } from "./useGuidedTrace";

const polyline = (points: Point[]) =>
  (points.length === 1 ? [...points, ...points] : points).map((p) => `${p.x},${p.y}`).join(" ");

export function HandWritingTraceGuide({
  text,
  onProgress,
}: {
  text: string;
  onProgress: (progress: {
    completed: number;
    total: number;
    done: boolean;
    charIndex: number;
  }) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<Box | null>(null);

  const trace = useGuidedTrace(text, box);
  const { layout, strokes, completed, progress, currentStroke, pen } = trace;

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setBox({ width, height });
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  const charIndex = currentStroke?.charIndex ?? (trace.done ? text.length : 0);
  useEffect(() => {
    onProgress({ completed, total: strokes.length, done: trace.done, charIndex });
  }, [completed, strokes.length, trace.done, charIndex, onProgress]);

  const pointFrom = (e: React.PointerEvent) => {
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const point = pointFrom(e);
    if (!point) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    trace.start(point);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const point = pointFrom(e);
    if (point) trace.move(point);
  };

  const fontSize = layout?.fontSize ?? 0;
  const stem = fontSize * STEM_EM;
  const trail = currentStroke ? pointsFrom(currentStroke, progress) : [];

  return (
    // The word is never panned by hand: it slides itself so the letter being traced stays on screen.
    <div ref={scrollerRef} className="absolute inset-0 overflow-hidden">
      <div
        ref={layerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={trace.end}
        onPointerCancel={trace.end}
        className="h-full touch-none select-none transition-transform duration-300 ease-out"
        style={{
          width: layout?.contentWidth ?? "100%",
          transform: `translateX(${-trace.offset}px)`,
        }}
      >
        {layout && (
          // biome-ignore lint/a11y/noSvgWithoutTitle: decorative tracing guide, the word is already shown above
          <svg className="h-full pointer-events-none" width={layout.contentWidth}>
            {/* Characters with no skeleton can't be traced, so they stand as the real glyph. */}
            {layout.untraced.map(({ char, x }) => (
              <text
                key={`${char}-${x}`}
                x={x}
                y={layout.baselineY}
                fill="#2626A5"
                style={{ fontFamily: WRITING_FONT, fontSize, fontWeight: 700 }}
              >
                {char}
              </text>
            ))}

            {/* The road to follow, then its dashed centre line. */}
            {strokes.map((stroke) => (
              <polyline
                key={`road-${stroke.charIndex}-${stroke.order}`}
                points={polyline(stroke.points)}
                fill="none"
                stroke="#EDF2FA"
                strokeWidth={stem}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
            {strokes.map((stroke) => (
              <polyline
                key={`dash-${stroke.charIndex}-${stroke.order}`}
                points={polyline(stroke.points)}
                fill="none"
                stroke="#B9CBE8"
                strokeWidth="2"
                strokeDasharray="7 6"
                strokeLinecap="round"
              />
            ))}

            {/* The traced letter: the same path, filled in as far as the pointer has come. */}
            {strokes.slice(0, completed).map((stroke) => (
              <polyline
                key={`ink-${stroke.charIndex}-${stroke.order}`}
                points={polyline(stroke.points)}
                fill="none"
                stroke="#2626A5"
                strokeWidth={stem}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
            {currentStroke && progress > 0 && (
              <polyline
                points={polyline(pointsUpTo(currentStroke, progress))}
                fill="none"
                stroke="#2626A5"
                strokeWidth={stem}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {currentStroke && pen && (
              <>
                {strokeLength(currentStroke) > 0 && (
                  <polyline
                    points={polyline(trail)}
                    fill="none"
                    stroke="#FF8500"
                    strokeWidth="2.5"
                    strokeDasharray="2 7"
                    strokeLinecap="round"
                    opacity={0.85}
                  />
                )}
                <StrokeStart
                  stroke={currentStroke}
                  pen={pen}
                  progress={progress}
                  fontSize={fontSize}
                />
              </>
            )}
          </svg>
        )}
      </div>
    </div>
  );
}

/** Pulsing numbered dot at the pen, with an arrow pointing where the stroke goes next. */
function StrokeStart({
  stroke,
  pen,
  progress,
  fontSize,
}: {
  stroke: TraceStroke;
  pen: Point;
  progress: number;
  fontSize: number;
}) {
  const radius = penMarkerRadius(fontSize);
  const next = stroke.points.find((_, i) => (stroke.lengths[i] as number) > progress);
  const span = next ? Math.hypot(next.x - pen.x, next.y - pen.y) : 0;
  const direction =
    next && span ? { x: (next.x - pen.x) / span, y: (next.y - pen.y) / span } : null;
  const tip = radius * 0.7;

  return (
    <g>
      <circle
        cx={pen.x}
        cy={pen.y}
        r={radius * 1.9}
        fill="#FF8500"
        opacity={0.25}
        className="animate-pulse"
      />
      <circle cx={pen.x} cy={pen.y} r={radius} fill="#FF8500" />
      <text
        x={pen.x}
        y={pen.y + radius * 0.52}
        textAnchor="middle"
        fill="#FFFFFF"
        style={{ fontFamily: WRITING_FONT, fontSize: radius * 1.5, fontWeight: 700 }}
      >
        {stroke.order}
      </text>
      {direction && (
        <polygon
          points={`0,${-tip} ${tip * 2},0 0,${tip}`}
          fill="#FF8500"
          transform={`translate(${pen.x + direction.x * radius * 2.6} ${pen.y + direction.y * radius * 2.6}) rotate(${(Math.atan2(direction.y, direction.x) * 180) / Math.PI})`}
        />
      )}
    </g>
  );
}
